import React, { useMemo, useState, useContext } from "react";

import { getSurfaceBorderColor } from "@/app/application/theme";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import type {
	ArchiveProject,
	ArchivedProjectsResponse,
} from "@/features/archive/model/archive/ArchivedProjectsRequest";
import {
	Box,
	Button,
	Chip,
	Divider,
	Skeleton,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);

type Mode = "total" | "avg";

type Props = {
	startDate: Date | null;
	endDate: Date | null;
	selectedUser: number;
	archivedProcedures?: ArchivedProjectsResponse | null;
	isLoading?: boolean;

	/**
	 * Optional: if you still want clicking a month to also update History range,
	 * keep this callback. We’ll call it when you click a month in month-view.
	 */
	onMonthClick?: (range: { start: Date; end: Date }) => void;

	/**
	 * Optional: if you want clicking a completion bar to do something (open modal, navigate),
	 * you can wire this up.
	 */
	onCompletionClick?: (completion: ArchiveProject) => void;
};

type ChecklistAgg = { key: string; name: string; minutes: number };

const TOP_N = 8; // fixed Top N + Other (no more Top4/6/10 UI)

function toMonthKey(dateIso: string, timezone: string) {
	return dayjs.utc(dateIso).tz(timezone).format("MMM YYYY");
}

function fmtMinutes(mins: number) {
	if (!Number.isFinite(mins)) return "—";
	if (mins <= 0) return "0 min";
	if (mins < 60) return `${Math.round(mins)} min`;
	const h = mins / 60;
	if (h < 10) return `${h.toFixed(1)} h`;
	return `${Math.round(h)} h`;
}

/**
 * For one completion:
 * group by checklist and compute duration = max(Date) - min(Date) in minutes.
 */
function computeChecklistDurationsForCompletion(
	p: ArchiveProject,
): ChecklistAgg[] {
	const updates: any[] = Array.isArray((p as any).Updates)
		? ((p as any).Updates as any[])
		: [];
	const map = new Map<string, { name: string; min: number; max: number }>();

	for (const u of updates) {
		const iso = u?.Date;
		const ms = iso ? new Date(iso).getTime() : NaN;
		if (!Number.isFinite(ms)) continue;

		const id = u?.CheckListId ?? 0;
		const name = String(u?.CheckListName ?? "—").trim() || "—";
		const key = `${id}::${name}`;

		const g = map.get(key);
		if (!g) map.set(key, { name, min: ms, max: ms });
		else {
			g.min = Math.min(g.min, ms);
			g.max = Math.max(g.max, ms);
		}
	}

	return Array.from(map.entries()).map(([key, g]) => ({
		key,
		name: g.name,
		minutes: Math.max(0, (g.max - g.min) / 60000),
	}));
}

export default function ArchiveChecklistStackedMonthChart({
	startDate,
	endDate,
	selectedUser,
	archivedProcedures,
	isLoading = false,
	onMonthClick,
	onCompletionClick,
}: Props) {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { timezone } = useTime();

	const [mode, setMode] = useState<Mode>("total");
	const [focusedMonth, setFocusedMonth] = useState<string | null>(null); // when set → show completion view

	// 1) Filter completions by user (same logic as History)
	const completions = useMemo(() => {
		const list = archivedProcedures?.ArchivedProjects ?? [];
		if (selectedUser === 0) return list;
		return list.filter((p) => p.Users?.some((u) => u.UserId === selectedUser));
	}, [archivedProcedures?.ArchivedProjects, selectedUser]);

	// 2) Build ordered month buckets for the range
	const months = useMemo(() => {
		if (!startDate || !endDate) return [];
		const start = dayjs(startDate).startOf("month");
		const end = dayjs(endDate).startOf("month");
		const res: { label: string; start: Date; end: Date }[] = [];
		let cur = start;

		while (!cur.isAfter(end)) {
			res.push({
				label: cur.format("MMM YYYY"),
				start: cur.startOf("month").toDate(),
				end: cur.endOf("month").toDate(),
			});
			cur = cur.add(1, "month");
		}
		return res;
	}, [startDate, endDate]);

	// 3) Map completions into month buckets
	const completionsByMonth = useMemo(() => {
		const map = new Map<string, ArchiveProject[]>();
		for (const m of months) map.set(m.label, []);

		for (const p of completions) {
			if (!p?.Created) continue;
			const mKey = toMonthKey(String(p.Created), timezone);
			if (!map.has(mKey)) continue;
			map.get(mKey)!.push(p);
		}

		// stable sort by created date asc
		for (const [k, arr] of map.entries()) {
			arr.sort(
				(a, b) => new Date(a.Created).getTime() - new Date(b.Created).getTime(),
			);
			map.set(k, arr);
		}
		return map;
	}, [completions, months, timezone]);

	// 4) Global Top N checklists (based on whichever view is active)
	//    - month view: compute across range
	//    - completion view: compute across completions in the focused month (better relevance)
	const { topKeys, keyToName } = useMemo(() => {
		const totals = new Map<string, { name: string; minutes: number }>();

		const sourceCompletions =
			focusedMonth && completionsByMonth.get(focusedMonth)
				? completionsByMonth.get(focusedMonth)!
				: completions;

		for (const p of sourceCompletions) {
			const durations = computeChecklistDurationsForCompletion(p);
			for (const d of durations) {
				const g = totals.get(d.key);
				if (!g) totals.set(d.key, { name: d.name, minutes: d.minutes });
				else g.minutes += d.minutes;
			}
		}

		const entries = Array.from(totals.entries())
			.map(([key, v]) => ({ key, name: v.name, minutes: v.minutes }))
			.sort((a, b) => b.minutes - a.minutes);

		const chosen = entries.slice(0, TOP_N);
		const keyToName = new Map<string, string>();
		for (const c of chosen) keyToName.set(c.key, c.name);

		return { topKeys: chosen.map((c) => c.key), keyToName };
	}, [completions, completionsByMonth, focusedMonth]);

	// 5A) Dataset for MONTH view
	const monthDataset = useMemo(() => {
		return months.map((m) => {
			const monthCompletions = completionsByMonth.get(m.label) ?? [];
			const completionCount = monthCompletions.length;

			const perChecklist = new Map<string, { name: string; minutes: number }>();

			for (const p of monthCompletions) {
				const durations = computeChecklistDurationsForCompletion(p);
				for (const d of durations) {
					const cur = perChecklist.get(d.key);
					if (!cur)
						perChecklist.set(d.key, { name: d.name, minutes: d.minutes });
					else cur.minutes += d.minutes;
				}
			}

			let otherMinutes = 0;
			const row: any = { x: m.label, completions: completionCount };

			for (const [key, v] of perChecklist.entries()) {
				if (topKeys.includes(key)) {
					row[key] =
						mode === "avg" && completionCount > 0
							? v.minutes / completionCount
							: v.minutes;
				} else {
					otherMinutes += v.minutes;
				}
			}

			row.__other__ =
				mode === "avg" && completionCount > 0
					? otherMinutes / completionCount
					: otherMinutes;

			for (const k of topKeys) if (row[k] === undefined) row[k] = 0;

			return row;
		});
	}, [months, completionsByMonth, topKeys, mode]);

	// 5B) Dataset for COMPLETION view (focused month)
	const completionDataset = useMemo(() => {
		if (!focusedMonth) return [];

		const monthCompletions = completionsByMonth.get(focusedMonth) ?? [];
		return monthCompletions.map((p, idx) => {
			const durations = computeChecklistDurationsForCompletion(p);

			// label: "13 Feb 12:08" (and add #n if duplicates)
			const createdLabel = p.Created
				? dayjs
						.utc(p.Created)
						.tz(timezone)
						.format(isMobile ? "DD/MM/YY HH:mm:ss" : "DD/MM/YY HH:mm:ss")
				: `Completion ${idx + 1}`;

			const row: any = {
				x: createdLabel,
				__completionId: p.Id,
				__completion: p,
				completions: 1,
			};

			let otherMinutes = 0;
			for (const d of durations) {
				if (topKeys.includes(d.key)) row[d.key] = (row[d.key] ?? 0) + d.minutes;
				else otherMinutes += d.minutes;
			}
			row.__other__ = otherMinutes;

			for (const k of topKeys) if (row[k] === undefined) row[k] = 0;

			return row;
		});
	}, [focusedMonth, completionsByMonth, topKeys, timezone, isMobile]);

	const dataset = focusedMonth ? completionDataset : monthDataset;

	const stackSeriesMeta = useMemo(() => {
		const primary = topKeys.map((key) => ({
			key,
			label: keyToName.get(key) ?? key,
		}));
		return [...primary, { key: "__other__", label: "Other" }];
	}, [topKeys, keyToName]);
	const tooltipMutedTextColor =
		theme.palette.mode === "dark"
			? alpha(theme.palette.text.secondary, 0.88)
			: alpha(theme.palette.text.secondary, 0.92);
	const tooltipShellSx = useMemo(
		() => ({
			renderMode: "html" as const,
			appendToBody: true,
			confine: false,
			enterable: true,
			transitionDuration: 0.08,
			backgroundColor:
				theme.palette.mode === "dark"
					? alpha(theme.palette.background.paper, 0.98)
					: alpha(theme.palette.background.paper, 0.98),
			borderColor: alpha(
				theme.palette.primary.main,
				theme.palette.mode === "dark" ? 0.45 : 0.22,
			),
			borderWidth: 1,
			borderRadius: 10,
			padding: [10, 12],
			textStyle: {
				color: theme.palette.text.primary,
				fontSize: 12,
			},
			extraCssText:
				`box-shadow: 0 10px 28px ${alpha(theme.palette.common.black, 0.22)}; max-width: 360px; white-space: normal; z-index: 9999; backdrop-filter: blur(2px);`,
		}),
		[theme],
	);
	const stackColors = useMemo(() => {
		const palette = [
			theme.palette.primary.main,
			theme.palette.success.main,
			theme.palette.warning.main,
			theme.palette.error.main,
			theme.palette.secondary.main,
			theme.palette.info.main,
			theme.palette.success.dark,
			theme.palette.warning.dark,
		];
		const otherColor =
			theme.palette.mode === "dark"
				? alpha(theme.palette.text.secondary, 0.76)
				: alpha(theme.palette.text.secondary, 0.7);
		return stackSeriesMeta.map((seriesMeta, index) =>
			seriesMeta.key === "__other__"
				? otherColor
				: palette[index % palette.length],
		);
	}, [stackSeriesMeta, theme]);
	const stackedChartOption = useMemo<echarts.EChartsOption>(() => {
		const categories = dataset.map((row) =>
			String((row as { x?: unknown }).x ?? ""),
		);

		return {
			backgroundColor: "transparent",
			color: stackColors,
			colorBy: "series",
			grid: { left: 56, right: 16, top: 20, bottom: 62 },
			tooltip: {
				...tooltipShellSx,
				trigger: "axis",
				axisPointer: { type: "shadow" },
				formatter: (params: unknown) => {
					const list = (Array.isArray(params) ? params : [params]) as Array<{
						dataIndex?: number;
						seriesName?: string;
						data?: number;
						marker?: string;
					}>;
					const first = list[0];
					const idx = Number(first?.dataIndex ?? 0);
					const pointLabel = categories[idx] ?? "";
					const completionsCount = Number(
						(dataset[idx] as { completions?: unknown } | undefined)
							?.completions ?? 0,
					);
					const rows = list
						.filter((item) => Number(item.data ?? 0) > 0)
						.map((item) => {
							const value = Number(item.data ?? 0);
							return `
								<div style="display:flex; justify-content:space-between; gap:12px;">
									<span>${item.marker ?? ""}${item.seriesName ?? ""}</span>
									<strong>${fmtMinutes(value)}</strong>
								</div>
							`;
						})
						.join("");

					return `
						<div style="max-width:340px">
							<div style="font-weight:700; margin-bottom:6px">${pointLabel}</div>
							${rows || `<div style="color:${tooltipMutedTextColor}">No checklist timing data</div>`}
							<div style="margin-top:6px; color:${tooltipMutedTextColor}; font-size:12px;">
								${
									focusedMonth
										? "Click this bar to open completion details"
										: `${completionsCount} completion${completionsCount === 1 ? "" : "s"} in this month`
								}
							</div>
						</div>
					`;
				},
			},
			xAxis: {
				type: "category",
				data: categories,
				axisLabel: {
					interval: 0,
					rotate: focusedMonth ? -35 : -30,
					color: theme.palette.text.secondary,
					fontSize: isMobile ? 10 : 12,
				},
				axisLine: { show: false },
				axisTick: { show: false },
			},
			yAxis: {
				type: "value",
				min: 0,
				splitLine: {
					show: true,
					lineStyle: { color: alpha(theme.palette.divider, 0.24) },
				},
				axisLabel: {
					color: theme.palette.text.secondary,
					fontSize: 11,
					formatter: (value: number) => fmtMinutes(Number(value)),
				},
				axisLine: { show: false },
				axisTick: { show: false },
			},
			series: stackSeriesMeta.map((seriesMeta, index) => ({
				name: seriesMeta.label,
				type: "bar",
				stack: "time",
				barMaxWidth: focusedMonth ? 22 : 28,
				itemStyle: {
					color: stackColors[index],
					borderColor: alpha(stackColors[index], 0.85),
					borderWidth: 0.2,
					borderRadius: index === stackSeriesMeta.length - 1 ? [4, 4, 0, 0] : 0,
				},
				emphasis: {
					focus: "series",
					itemStyle: {
						opacity: 0.9,
					},
				},
				data: dataset.map((row) => {
					const val = (row as Record<string, unknown>)[seriesMeta.key];
					return Number(val ?? 0);
				}),
			})),
		};
	}, [
		dataset,
		focusedMonth,
		isMobile,
		stackColors,
		stackSeriesMeta,
		theme.palette.divider,
		theme.palette.text.secondary,
		tooltipMutedTextColor,
		tooltipShellSx,
	]);
	const handleChartClick = useMemo(
		() => (params: unknown) => {
			const clickParams = params as { dataIndex?: number };
			const idx = Number(clickParams?.dataIndex);
			if (!Number.isFinite(idx)) return;

			if (!focusedMonth) {
				const month = months[idx];
				if (!month) return;
				setFocusedMonth(month.label);
				onMonthClick?.({ start: month.start, end: month.end });
				return;
			}

			const row = dataset[idx] as { __completion?: ArchiveProject } | undefined;
			const completion = row?.__completion;
			if (completion && onCompletionClick) onCompletionClick(completion);
		},
		[dataset, focusedMonth, months, onCompletionClick, onMonthClick],
	);
	const chartEvents = useMemo(
		() => ({
			click: handleChartClick,
		}),
		[handleChartClick],
	);

	const hasNoData =
		!isLoading &&
		dataset.length > 0 &&
		dataset.every(
			(r: any) =>
				(Number(r.__other__) ?? 0) === 0 &&
				topKeys.every((k) => (Number(r[k]) ?? 0) === 0),
		);

	return (
		<Box sx={{ width: "100%", minWidth: 0 }}>
			{/* Header */}
			<Stack sx={{ px: { xs: 1.25, sm: 1.5 }, pt: 1.25 }} spacing={1}>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					alignItems={{ xs: "stretch", sm: "center" }}
					justifyContent="space-between"
					gap={1}
				>
					<Box sx={{ minWidth: 0 }}></Box>

					<Stack
						direction="row"
						spacing={1}
						alignItems="center"
						justifyContent={{ xs: "flex-start", sm: "flex-end" }}
						flexWrap="wrap"
						useFlexGap
					>
						{focusedMonth ? (
							<Button
								size="small"
								variant="outlined"
								onClick={() => setFocusedMonth(null)}
								sx={{
									textTransform: "none",
									fontWeight: 900,
									borderRadius: 2,
									px: 1.25,
									borderColor: alpha(theme.palette.primary.main, 0.2),
								}}
							>
								←{" "}
								<Translate String="Archive.Chart.BackToMonths">
									Back to months
								</Translate>
							</Button>
						) : (
							<ToggleButtonGroup
								size="small"
								value={mode}
								exclusive
								onChange={(_, v) => v && setMode(v)}
								sx={{
									"& .MuiToggleButton-root": {
										textTransform: "none",
										fontWeight: 900,
										borderRadius: 2,
										px: 1.25,
										borderColor: `${alpha(theme.palette.divider, 0.9)} !important`,
										backgroundColor: "background.paper",
									},
									"& .MuiToggleButton-root.Mui-selected": {
										backgroundColor: alpha(theme.palette.primary.main, 0.1),
										color: "primary.main",
										borderColor: `${alpha(theme.palette.primary.main, 0.2)} !important`,
									},
								}}
							>
								<ToggleButton value="total">
									<Translate String="Archive.Chart.Total">Total</Translate>
								</ToggleButton>
								<ToggleButton value="avg">
									<Translate String="Archive.Chart.Avg">Avg</Translate>
								</ToggleButton>
							</ToggleButtonGroup>
						)}
					</Stack>
				</Stack>

				<Divider sx={{ opacity: 0.7 }} />

				{/* Compact legend chips */}
				<Stack
					direction="row"
					flexWrap="wrap"
					gap={0.75}
					alignItems="center"
					useFlexGap
				>
					<Chip
						size="small"
						variant="outlined"
						label={
							<Typography variant="caption" sx={{ fontWeight: 900 }}>
								<Translate String="Archive.Chart.TopNPlusOther">
									Top {TOP_N} + Other
								</Translate>
							</Typography>
						}
						sx={{ fontWeight: 900, borderRadius: 2, opacity: 0.5 }}
					/>
					{topKeys.slice(0, isMobile ? 4 : 8).map((k) => (
						<Chip
							key={k}
							size="small"
							variant="outlined"
							label={keyToName.get(k) ?? k}
							sx={{
								maxWidth: 320,
								borderRadius: 2,
								fontWeight: 800,
								"& .MuiChip-label": {
									overflow: "hidden",
									textOverflow: "ellipsis",
								},
							}}
						/>
					))}
					<Chip
						size="small"
						variant="outlined"
						label={GetLanguageValueAsString(translation, "Auto.Features.Archive.Pages.ArchiveProcedure.ArchiveChecklistStackedMonthChart.L607C13.Other", "Other")}
						sx={{ fontWeight: 800, opacity: 0.5, borderRadius: 2 }}
					/>
				</Stack>
			</Stack>

			{/* Chart body */}
			<Box sx={{ px: { xs: 0.75, sm: 1 }, pb: 1.25, pt: 0.5 }}>
				{isLoading ? (
					<Box sx={{ mx: { xs: 0.5, sm: 1 } }}>
						<Skeleton
							variant="rounded"
							animation="wave"
							height={isMobile ? 240 : 280}
						/>
					</Box>
				) : hasNoData ? (
					<Box
						sx={{
							height: { xs: 140, sm: 170 },
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							borderRadius: 2,
							mx: { xs: 0.5, sm: 1 },
							backgroundColor: alpha(theme.palette.primary.main, 0.04),
							border: `1px dashed ${getSurfaceBorderColor(theme, "strong")}`,
						}}
					>
						<Box sx={{ textAlign: "center" }}>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ fontWeight: 800 }}
							>
								<Translate String="Archive.Chart.NoData">
									No checklist timing data in this range
								</Translate>
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", mt: 0.25 }}
							>
								<Translate String="Archive.Chart.NoDataHint">
									Try a different range or user filter
								</Translate>
							</Typography>
						</Box>
					</Box>
				) : (
					<Box
						sx={{
							width: "100%",
							overflowX: "hidden",
							borderRadius: 2.5,
							border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
							backgroundColor: alpha(
								theme.palette.background.default,
								theme.palette.mode === "dark" ? 0.4 : 0.72,
							),
							px: 0.5,
							py: 0.75,
						}}
					>
						<ReactECharts
							notMerge
							opts={{ renderer: "canvas" }}
							option={stackedChartOption}
							onEvents={chartEvents}
							style={{
								height: isMobile ? 240 : 280,
								width: "100%",
							}}
						/>
					</Box>
				)}
			</Box>

			{/* Footer hint */}
			<Box sx={{ px: { xs: 1.25, sm: 1.5 }, pb: 1.25 }}>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", fontWeight: 700 }}
				>
					{focusedMonth ? (
						<Translate String="Archive.Chart.ClickCompletionHint">
							Tip: click a completion bar to open its details (if enabled).
						</Translate>
					) : (
						<Translate String="Archive.Chart.ClickMonthHint">
							Click a month to see every completion separately.
						</Translate>
					)}
				</Typography>
			</Box>
		</Box>
	);
}
